import { api } from "@/lib/api";
import type { Page } from "@/features/users/api";

export type ExpiryStatus = "none" | "active" | "expiring_soon" | "expired";
export type AssetType = "hardware" | "software" | "network" | "peripheral" | "other";
export type AssetStatus = "active" | "in_repair" | "retired" | "lost";
export type ContractType = "amc" | "warranty" | "extended_warranty";

export interface AssetListItem {
  id: string;
  customer_id: string;
  customer_name: string | null;
  asset_tag: string;
  name: string;
  asset_type: AssetType;
  category: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  status: AssetStatus;
  location_id: string | null;
  location_name: string | null;
  warranty_expiry_date: string | null;
  warranty_status: ExpiryStatus;
  days_to_warranty_expiry: number | null;
  amc_contract_id: string | null;
  amc_contract_number: string | null;
}

export interface AssetDetail extends AssetListItem {
  purchase_date: string | null;
  purchase_cost: string | null;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
}

export interface AssetBody {
  customer_id?: string;
  asset_tag?: string | null;
  name: string;
  asset_type: AssetType;
  category?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  status?: AssetStatus;
  purchase_date?: string | null;
  purchase_cost?: string | null;
  warranty_expiry_date?: string | null;
  assigned_to?: string | null;
  location_id?: string | null;
  amc_contract_id?: string | null;
  notes?: string | null;
}

export interface AssetFilters {
  page?: number;
  page_size?: number;
  search?: string;
  customer_id?: string;
  asset_type?: string;
  status?: string;
  amc_contract_id?: string;
  warranty_expiring_days?: number;
}

export interface ContractListItem {
  id: string;
  customer_id: string;
  customer_name: string | null;
  contract_number: string;
  provider: string | null;
  contract_type: ContractType;
  start_date: string;
  end_date: string;
  cost: string | null;
  is_active: boolean;
  coverage_status: ExpiryStatus;
  days_to_expiry: number | null;
  asset_count: number;
}

export interface ContractDetail extends ContractListItem {
  coverage_terms: string | null;
  notes: string | null;
  created_at: string;
}

export interface ContractBody {
  customer_id?: string;
  contract_number: string;
  provider?: string | null;
  contract_type: ContractType;
  start_date: string;
  end_date: string;
  cost?: string | null;
  coverage_terms?: string | null;
  is_active?: boolean;
  notes?: string | null;
}

export interface ContractFilters {
  page?: number;
  page_size?: number;
  search?: string;
  customer_id?: string;
  contract_type?: string;
  active_only?: boolean;
  expiring_days?: number;
}

export interface LinkedAsset {
  id: string;
  asset_tag: string;
  name: string;
  asset_type: AssetType;
  status: AssetStatus;
  serial_number: string | null;
  warranty_status: ExpiryStatus;
  linked_at: string;
}

function qs(filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "" && v !== false) params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

// --- assets ---

export function listAssets(filters: AssetFilters = {}): Promise<Page<AssetListItem>> {
  return api.get<Page<AssetListItem>>(`/assets${qs(filters)}`);
}

export function getAsset(id: string): Promise<AssetDetail> {
  return api.get<AssetDetail>(`/assets/${id}`);
}

export function createAsset(body: AssetBody): Promise<AssetDetail> {
  return api.post<AssetDetail>("/assets", body);
}

export function updateAsset(id: string, body: Partial<AssetBody>): Promise<AssetDetail> {
  return api.patch<AssetDetail>(`/assets/${id}`, body);
}

export function deleteAsset(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/assets/${id}`);
}

// --- AMC contracts ---

export function listContracts(filters: ContractFilters = {}): Promise<Page<ContractListItem>> {
  return api.get<Page<ContractListItem>>(`/amc-contracts${qs(filters)}`);
}

export function createContract(body: ContractBody): Promise<ContractDetail> {
  return api.post<ContractDetail>("/amc-contracts", body);
}

export function updateContract(
  id: string,
  body: Partial<ContractBody>,
): Promise<ContractDetail> {
  return api.patch<ContractDetail>(`/amc-contracts/${id}`, body);
}

export function deleteContract(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/amc-contracts/${id}`);
}

// --- ticket <-> asset links ---

export function ticketAssets(ticketId: string): Promise<LinkedAsset[]> {
  return api.get<LinkedAsset[]>(`/tickets/${ticketId}/assets`);
}

export function linkTicketAsset(ticketId: string, assetId: string): Promise<LinkedAsset> {
  return api.post<LinkedAsset>(`/tickets/${ticketId}/assets`, { asset_id: assetId });
}

export function unlinkTicketAsset(
  ticketId: string,
  assetId: string,
): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/tickets/${ticketId}/assets/${assetId}`);
}
